<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { accountScopedUrl } = useAccount();

const filter = ref('pending');
const statusFilterOptions = ['pending', 'done', 'cancelled'];

const records = computed(() => getters['tasks/getAccountTasks'].value);
const uiFlags = computed(() => getters['tasks/getUIFlags'].value);

const isOverdue = task =>
  task.status === 'pending' && task.due_at && new Date(task.due_at) < new Date();

const statusBadgeClass = task => {
  if (task.status === 'done') return 'bg-n-teal-3 text-n-teal-11';
  if (task.status === 'cancelled') return 'bg-n-slate-3 text-n-slate-11';
  return isOverdue(task)
    ? 'bg-n-ruby-3 text-n-ruby-11'
    : 'bg-n-amber-3 text-n-amber-11';
};

const tableHeaders = computed(() => [
  t('SETTINGS_TASKS.LIST.TABLE_HEADER.STATUS'),
  t('SETTINGS_TASKS.LIST.TABLE_HEADER.TITLE'),
  t('SETTINGS_TASKS.LIST.TABLE_HEADER.CONVERSATION'),
  t('SETTINGS_TASKS.LIST.TABLE_HEADER.ASSIGNEE'),
  t('SETTINGS_TASKS.LIST.TABLE_HEADER.DUE_AT'),
  t('SETTINGS_TASKS.LIST.TABLE_HEADER.ACTION'),
]);

const fetchTasks = () => {
  store.dispatch('tasks/getAll', { status: filter.value || undefined });
};

const completeTask = async id => {
  try {
    await store.dispatch('tasks/updateAccountWide', { id, status: 'done' });
    fetchTasks();
  } catch (error) {
    useAlert(t('SETTINGS_TASKS.UPDATE_ERROR'));
  }
};

const deleteTask = async id => {
  try {
    await store.dispatch('tasks/deleteAccountWide', id);
    useAlert(t('SETTINGS_TASKS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('SETTINGS_TASKS.DELETE_ERROR'));
  }
};

onMounted(fetchTasks);
watch(filter, fetchTasks);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingAll"
    :loading-message="t('SETTINGS_TASKS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="t('SETTINGS_TASKS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('SETTINGS_TASKS.HEADER')"
        :description="t('SETTINGS_TASKS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex items-center gap-2 mb-4">
        <Button
          v-for="option in statusFilterOptions"
          :key="option"
          :label="t(`SETTINGS_TASKS.LIST.STATUS.${option.toUpperCase()}`)"
          sm
          :faded="filter !== option"
          :slate="filter !== option"
          @click="filter = filter === option ? '' : option"
        />
      </div>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="t('SETTINGS_TASKS.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="task in items" :key="task.id" :item="task">
            <template #default>
              <BaseTableCell>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-semibold"
                  :class="statusBadgeClass(task)"
                >
                  {{
                    isOverdue(task)
                      ? t('SETTINGS_TASKS.LIST.STATUS.OVERDUE')
                      : t(`SETTINGS_TASKS.LIST.STATUS.${task.status.toUpperCase()}`)
                  }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 line-clamp-1">
                  {{ task.title }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <router-link
                  :to="accountScopedUrl(`conversations/${task.conversation_id}`)"
                  class="text-n-iris-9 hover:underline"
                >
                  #{{ task.conversation_id }}
                </router-link>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ task.assignee?.name || '—' }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ task.due_at ? new Date(task.due_at).toLocaleString() : '—' }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <Button
                  v-if="task.status === 'pending'"
                  v-tooltip.top="t('SETTINGS_TASKS.COMPLETE')"
                  icon="i-lucide-check"
                  slate
                  sm
                  class="hover:enabled:text-n-teal-11 hover:enabled:bg-n-teal-2"
                  @click="completeTask(task.id)"
                />
                <Button
                  v-tooltip.top="t('SETTINGS_TASKS.DELETE')"
                  icon="i-lucide-x"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click="deleteTask(task.id)"
                />
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>
  </SettingsLayout>
</template>
